import { registerChart } from '@/chart'
import { ThemeProvider } from '@/components/providers/theme-provider'
import { getConfig, getEnvironment } from '@/config'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { EvmService } from '@trinity/blockchain'
import { EvmManagementService } from '@trinity/blockchain-management'
import { BlockchainManagementServiceProvider } from '@trinity/blockchain-management-react'
import { BlockchainServiceProvider } from '@trinity/blockchain-react'
import { PharaohSwap } from '@trinity/pharaoh'
import { PharaohServiceProvider } from '@trinity/pharaoh-react'
import { InjectedWallet } from '@trinity/wallet'
import { WalletProvider } from '@trinity/wallet-react'
import { type FC, type ReactNode, useEffect, useMemo, useState } from 'react'

registerChart()

interface Props {
    children: ReactNode
}

const queryClient = new QueryClient()

const environment = getEnvironment()
const config = getConfig(environment, import.meta.env)

export const Providers: FC<Props> = ({ children }) => {
    const [wallet, setWallet] = useState<InjectedWallet>(new InjectedWallet(window.ethereum))

    useEffect(() => {
        const wallet = new InjectedWallet(window.ethereum)
        setWallet(wallet)
        return () => {
            wallet.cleanup()
        }
    }, [])

    const blockchainService = useMemo(
        () =>
            new EvmService(wallet, {
                contractAddresses: config.contractAddresses,
            }),
        [wallet],
    )

    const pharaohService = useMemo(
        () =>
            new PharaohSwap(
                wallet.getProvider(),
                {
                    V3_FACTORY: config.pharaoh.contracts.V3_FACTORY,
                    V3_SWAP_ROUTER: config.pharaoh.contracts.V3_SWAP_ROUTER,
                    QUOTER_V1: config.pharaoh.contracts.QUOTER_V1,
                    QUOTER_V2: config.pharaoh.contracts.QUOTER_V2,
                    POSITION_MANAGER: config.pharaoh.contracts.POSITION_MANAGER,
                },
                {
                    AVAX: config.pharaoh.tokens.AVAX,
                    WAVAX: config.pharaoh.tokens.WAVAX,
                    UTY: config.pharaoh.tokens.UTY,
                    USDT: config.pharaoh.tokens.USDT,
                },
            ),
        [wallet],
    )

    const blockchainManagementService = useMemo(
        () => new EvmManagementService(wallet, { contractAddresses: config.contractAddresses }),
        [wallet],
    )

    return (
        <QueryClientProvider client={queryClient}>
            <WalletProvider wallet={wallet}>
                <BlockchainServiceProvider blockchainService={blockchainService}>
                    <BlockchainManagementServiceProvider blockchainManagementService={blockchainManagementService}>
                        <PharaohServiceProvider pharaohService={pharaohService}>
                            <ThemeProvider>{children}</ThemeProvider>
                        </PharaohServiceProvider>
                    </BlockchainManagementServiceProvider>
                </BlockchainServiceProvider>
            </WalletProvider>
        </QueryClientProvider>
    )
}
